import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface BucketListItem {
  id: string;
  title: string;
  description?: string;
  priority: number;
  assigned_date?: string;
  assigned_hour?: number;
  linked_task_id?: string;
  is_completed: boolean;
  completion_percentage: number;
  created_at: string;
}

interface BucketListProps {
  isOpen: boolean;
  onClose: () => void;
}

const BucketList: React.FC<BucketListProps> = ({ isOpen, onClose }) => {
  const [items, setItems] = useState<BucketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [assigningItem, setAssigningItem] = useState<string | null>(null);
  const [assignDate, setAssignDate] = useState('');
  const [assignHour, setAssignHour] = useState(9);
  const [error, setError] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getBucketList(showCompleted);
      setItems(response.items || []);
    } catch (err) {
      console.error('Failed to fetch bucket list:', err);
      setError('Failed to load bucket list');
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen, fetchItems]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemTitle.trim()) return;

    try {
      await apiService.createBucketListItem({ title: newItemTitle.trim() });
      setNewItemTitle('');
      fetchItems();
    } catch (err) {
      setError('Failed to add item');
    }
  };

  const handleAssignDate = async (itemId: string) => {
    if (!assignDate) return;

    try {
      await apiService.assignBucketListDate(itemId, assignDate, assignHour);
      setAssigningItem(null);
      setAssignDate('');
      setAssignHour(9);
      fetchItems();
    } catch (err) {
      setError('Failed to assign date');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Delete this item?')) return;

    try {
      await apiService.deleteBucketListItem(itemId);
      fetchItems();
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handleToggleComplete = async (item: BucketListItem) => {
    try {
      await apiService.updateBucketListItem(item.id, { is_completed: !item.is_completed });
      fetchItems();
    } catch (err) {
      setError('Failed to update item');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'linear-gradient(145deg, #fefefe 0%, #f8f5f0 100%)',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>Bucket List</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '0.875rem' }}>
              Capture your ideas, schedule them later
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              fontSize: '1.25rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ✕
          </button>
        </div>

        {/* Add New Item Form */}
        <form onSubmit={handleAddItem} style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              placeholder="Add a new task to your bucket list..."
              style={{
                flex: 1,
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary-color, #667eea)'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            <button
              type="submit"
              disabled={!newItemTitle.trim()}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                opacity: newItemTitle.trim() ? 1 : 0.5
              }}
            >
              + Add
            </button>
          </div>
        </form>

        {/* Filter */}
        <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: '#f9fafb' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show completed</span>
          </label>
          <span style={{ marginLeft: 'auto', fontSize: '0.875rem', color: '#9ca3af' }}>
            {items.length} items
          </span>
        </div>

        {error && (
          <div style={{ padding: '12px 24px', background: '#fef2f2', color: '#dc2626', fontSize: '0.875rem' }}>
            {error}
          </div>
        )}

        {/* Items List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📋</div>
              <p>Your bucket list is empty</p>
              <p style={{ fontSize: '0.875rem' }}>Add tasks above to get started!</p>
            </div>
          ) : (
            <div style={{ padding: '8px 0' }}>
              {items.map((item, index) => (
                <div
                  key={item.id}
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: item.is_completed ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={item.is_completed}
                      onChange={() => handleToggleComplete(item)}
                      style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer' }}
                    />

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 500, 
                        textDecoration: item.is_completed ? 'line-through' : 'none',
                        color: item.is_completed ? '#9ca3af' : '#1f2937'
                      }}>
                        {item.title}
                      </div>
                      
                      {/* Status badges */}
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {item.assigned_date && (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            background: '#dbeafe',
                            color: '#1e40af'
                          }}>
                            📅 {item.assigned_date}
                          </span>
                        )}
                        {item.linked_task_id && (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            background: '#d1fae5',
                            color: '#065f46'
                          }}>
                            ✓ Linked ({item.completion_percentage}%)
                          </span>
                        )}
                      </div>

                      {/* Assign date form */}
                      {assigningItem === item.id && (
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="date"
                            value={assignDate}
                            onChange={(e) => setAssignDate(e.target.value)}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }}
                          />
                          <select
                            value={assignHour}
                            onChange={(e) => setAssignHour(parseInt(e.target.value))}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              backgroundColor: 'white'
                            }}
                          >
                            {Array.from({ length: 24 }, (_, i) => {
                              const period = i >= 12 ? 'PM' : 'AM';
                              const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
                              return (
                                <option key={i} value={i}>
                                  {displayHour}:00 {period}
                                </option>
                              );
                            })}
                          </select>
                          <button
                            onClick={() => handleAssignDate(item.id)}
                            disabled={!assignDate}
                            style={{
                              padding: '8px 16px',
                              background: 'var(--primary-color, #667eea)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              cursor: 'pointer'
                            }}
                          >
                            Assign
                          </button>
                          <button
                            onClick={() => { setAssigningItem(null); setAssignDate(''); setAssignHour(9); }}
                            style={{
                              padding: '8px 12px',
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {!item.assigned_date && !item.is_completed && (
                        <button
                          onClick={() => setAssigningItem(item.id)}
                          title="Schedule this task"
                          style={{
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          📅
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        title="Delete"
                        style={{
                          padding: '6px 10px',
                          background: '#fef2f2',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BucketList;
